import React, { useState, useEffect } from 'react';
import DocumentOptions from './DocumentOptions';
import Icon from '../Icon';
 
import { debug } from '../../helpers/debug';
import ParagraphOptions from './ParagraphOptions';
import HeadingOptions from './HeadingOptions';
import ListOptions from './ListOptions';
import BlockquoteOptions from './BlockquoteOptions';
import CodeBlockOptions from './CodeBlockOptions';
import LinkOptions from './LinkOptions';

export default function OptionsPanel({ editor, open, onClose, fixed, onToggleFixed, docId, editorRef, isVersionDoc = false }) {
  const [activeElement, setActiveElement] = useState('document');
  const [isReady, setIsReady] = useState(false);

  // Reset to document view when docId changes
  useEffect(() => {
    setActiveElement('document');
  }, [docId]);

  useEffect(() => {
    if (!editor) {
      setIsReady(false);
      return;
    }

    // Check if editor view is available
    const checkReady = () => {
      try {
        // Try to actually access the dom to verify it doesn't throw
        const dom = editor.view?.dom;
        if (dom) {
          setIsReady(true);
          return true;
        }
      } catch (e) {
        // Editor not ready yet
        debug('Editor not ready:', { type: 'warn' }, e.message);
      }
      setIsReady(false);
      return false;
    };

    // Reset ready state when editor changes
    setIsReady(false);

    // Try immediately
    if (!checkReady()) {
      // If not ready, wait a bit and try again
      const timeout = setTimeout(checkReady, 100);
      return () => clearTimeout(timeout);
    }
  }, [editor]);

  useEffect(() => {
    if (!editor || !isReady) return;

    const updateActiveElement = () => {
      // Check if editor is focused (support both boolean and function APIs)
      const editorIsFocused = typeof editor.isFocused === 'function' ? editor.isFocused() : !!editor.isFocused;
      if (!editorIsFocused) {
        setActiveElement('document');
        return;
      }

      // Check for link
      if (editor.isActive('link')) {
        setActiveElement('link');
        return;
      }

      // Check for block elements from innermost to outermost
      if (editor.isActive('codeBlock')) {
        setActiveElement('codeBlock');
      } else if (editor.isActive('blockquote')) {
        setActiveElement('blockquote');
      } else if (editor.isActive('bulletList') || editor.isActive('orderedList') || editor.isActive('taskList')) {
        setActiveElement('list');
      } else if (editor.isActive('heading')) {
        setActiveElement('heading');
      } else {
        // If none of the above but editor is focused, it's likely a paragraph
        setActiveElement('paragraph');
      }
    };

    // Update on selection change
    // Only subscribe if the editor exposes event methods
    if (editor.on && editor.off) {
      editor.on('selectionUpdate', updateActiveElement);
      editor.on('update', updateActiveElement);
      editor.on('focus', updateActiveElement);
      editor.on('blur', updateActiveElement);
    }

    // Initial update
    updateActiveElement();

    return () => {
      if (editor.off && editor.on) {
        editor.off('selectionUpdate', updateActiveElement);
        editor.off('update', updateActiveElement);
        editor.off('focus', updateActiveElement);
        editor.off('blur', updateActiveElement);
      }
    };
  }, [editor, isReady]);

  const renderOptions = () => {
    switch (activeElement) {
      case 'link':
        return <LinkOptions editor={editor} />;
      case 'codeBlock':
        return <CodeBlockOptions editor={editor} />;
      case 'blockquote':
        return <BlockquoteOptions editor={editor} />;
      case 'list':
        return <ListOptions editor={editor} />;
      case 'heading':
        return <HeadingOptions editor={editor} />;
      case 'paragraph':
        return <ParagraphOptions editor={editor} />;
      case 'document':
      default:
        return <DocumentOptions editor={editor} docId={docId} editorRef={editorRef} />;
    }
  };

  return (
    <div id="optionsPanel" className={`${open ? 'open' : ''} ${fixed ? 'fixed' : 'overlay'}`}>
      <div id="optionsHeader">
        <h3>Options</h3>
        <button id="optionsButtonClose" className="iconButton" onClick={onClose}
          aria-label="Close options" data-tooltip="Close options" data-tooltip-placement="top">
          <Icon icon={'x'} className="icon" />
        </button>
      </div>
      <div id="optionsContent">
        {open && (
          isVersionDoc ? (
            <div className="options-section">
              <p className="placeholder">Version previews are read-only.</p>
            </div>
          ) : !editor || !isReady ? (
            <div className="options-section">
              <p className="placeholder">Waiting for document...</p>
            </div>
          ) : (
            renderOptions()
          )
        )}
      </div>
      <div id="optionsFooter">
        <button 
          className={`iconButton medium`}
          onClick={onToggleFixed}
          aria-label={fixed ? 'Unpin panel' : 'Pin panel'}
          data-tooltip={fixed ? 'Unpin panel' : 'Pin panel'}
          data-tooltip-placement="top"
        >
          <Icon icon={fixed ? 'push-pin' : 'push-pin-slash'} className="icon medium" />
        </button>
      </div>
    </div>
  );
}
